import { useRef, useEffect, useState } from 'react';

import {
  useAuth,
  useConvert,
  useAuthRefresh,
  useFrozenOAuth,
  useConversionMode,
  useConverterParams,
  useConversionParams,
} from '@/hooks';
import {
  Alert,
  Loader,
  URLInput,
  OAuthForm,
  SupportCard,
  TargetSelect,
  ConvertButton,
  DetailsToggle,
  ErrorRenderer,
  HoneypotInput,
  PrivacyToggle,
  PrimaryAuthForm,
  PlaylistRenderer,
  NewConversionButton,
  ConversionModeSelect,
} from '@/components';
import { isApple, getPlatformByUrl, getPlaylistIdFromUrl } from '@/utils';
import { CONVERSION, CONVERSION_MODE, PLATFORM_CAPTIONS } from '@/constants';

const DEV_PLAYLIST_URL = import.meta.env.VITE_DEV_PLAYLIST_URL || '';

const { single } = CONVERSION;

const { default: defaultMode } = CONVERSION_MODE;

const SingleConverter = () => {
  const { sourceParam, targetParam } = useConversionParams();

  const type = CONVERSION.single;
  const [isPrivate, setIsPrivate] = useState(true);
  const [source, setSource] = useState(sourceParam);
  const [botDetected, setBotDetected] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);

  const {
    target,
    isActive,
    setTarget,
    source: sourceUrl,
    setSource: setSourceUrl,
  } = useConverterParams(single, {
    target: { routeParam: targetParam },
    source: { default: DEV_PLAYLIST_URL },
  });

  const { isAuthorized, onUnauthorized } = useAuth();

  const sourceId = getPlaylistIdFromUrl(source, sourceUrl);

  const {
    mode,
    setMode,
    wouldSkipAll,
    priorConversions,
    hasPriorConversions,
    isLoading: isLoadingPriors,
  } = useConversionMode({ source, target, sourceId });

  const {
    error,
    isFrozen,
    progress,
    conversion,
    isComplete,
    isConverting,
    handleConvert,
    clearConversion,
  } = useConvert({
    mode,
    type,
    source,
    target,
    sourceUrl,
    isPrivate,
    onUnauthorized,
  });

  const { targetAuth } = useFrozenOAuth({ target, isFrozen });

  useAuthRefresh(target, targetAuth, isAuthorized, !isActive);

  const { isSignedIn, isSigningIn, error: oAuthError } = targetAuth || {};

  const prevSourceUrl = useRef(sourceUrl);

  useEffect(() => {
    if (prevSourceUrl.current !== sourceUrl) {
      setTarget(targetParam);
      prevSourceUrl.current = sourceUrl;
    }
    setSource(getPlatformByUrl(sourceUrl));
  }, [sourceUrl]);

  const handleStartNew = () => {
    clearConversion();
    setSourceUrl('');
    setMode(defaultMode);
  };

  const handleSourceUrlChange = (url) => {
    setSourceUrl(url);
  };

  const handleTargetChange = (platform) => {
    setIsPrivate(true);
    setTarget(platform);
  };

  const targetCaption = PLATFORM_CAPTIONS[target];

  const showDetailsBody = !isComplete || isDetailsOpen;

  const isPrivacyToggleDisabled =
    isFrozen || wouldSkipAll || isApple(target);

  const isConvertButtonDisabled =
    isComplete ||
    !isSignedIn ||
    isSigningIn ||
    isConverting ||
    wouldSkipAll;

  return (
    <>
      <HoneypotInput onBotDetected={setBotDetected} />

      {isAuthorized ? (
        <>
          <DetailsToggle
            isComplete={isComplete}
            isConverting={isConverting}
            isDetailsOpen={isDetailsOpen}
            setIsDetailsOpen={setIsDetailsOpen}
          />

          {showDetailsBody && (
            <div className={isComplete ? 'mt-3' : 'mb-3'}>
              <URLInput
                url={sourceUrl}
                source={source}
                caption="playlist"
                disabled={isFrozen}
                requiredSource={sourceParam}
                onUrlChange={handleSourceUrlChange}
              />

              {(!sourceParam || source === sourceParam) && (
                <>
                  {source !== null && !targetParam && (
                    <TargetSelect
                      id={single}
                      source={source}
                      target={target}
                      isConverting={isConverting}
                      onTargetChange={handleTargetChange}
                      disabled={isFrozen || !!targetParam}
                    />
                  )}

                  {source && target && (
                    <>
                      <div className="oauth-group">
                        <OAuthForm
                          {...targetAuth}
                          platform={target}
                          disabled={isFrozen}
                        />
                      </div>

                      {isSignedIn &&
                        (isLoadingPriors ? (
                          <div className="mt-3">
                            <Loader />
                          </div>
                        ) : (
                          <>
                            {hasPriorConversions && (
                              <div className="mt-3">
                                <ConversionModeSelect
                                  value={mode}
                                  onChange={setMode}
                                  disabled={isFrozen}
                                  sourceIds={[sourceId]}
                                  isComplete={isComplete}
                                  wouldSkipAll={wouldSkipAll}
                                  priorConversions={priorConversions}
                                />
                              </div>
                            )}

                            <div className="privacy-convert-group mt-3">
                              <PrivacyToggle
                                type={type}
                                isPrivate={isPrivate}
                                onToggle={setIsPrivate}
                                disabled={isPrivacyToggleDisabled}
                              />

                              <ConvertButton
                                onConvert={handleConvert}
                                disabled={isConvertButtonDisabled}
                              />
                            </div>
                          </>
                        ))}

                      {oAuthError ? (
                        <Alert type="danger">{oAuthError}</Alert>
                      ) : (
                        <>
                          {!isSignedIn && !isSigningIn && !isFrozen && (
                            <Alert type="info">
                              Connect to {targetCaption} to allow us to create
                              the playlist on your account.
                            </Alert>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {(isComplete || isConverting) && (
            <PlaylistRenderer
              {...conversion}
              source={source}
              target={target}
              progress={progress}
              isConverting={isConverting}
            />
          )}

          {error && <ErrorRenderer error={error} />}

          {isComplete && (
            <>
              <SupportCard />

              <NewConversionButton onStartNew={handleStartNew} />
            </>
          )}
        </>
      ) : (
        <PrimaryAuthForm botDetected={botDetected} />
      )}
    </>
  );
};

export default SingleConverter;
